import React, { useCallback, useState } from 'react';
import {
  Alert,
  Bullseye,
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  EmptyState,
  EmptyStateBody,
  Flex,
  FlexItem,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from '@patternfly/react-core';
import { ActionsColumn, type IAction, Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CreditCardIcon, EllipsisVIcon, PlusCircleIcon, SyncAltIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useImpersonation } from '../../../contexts/impersonation/ImpersonationContext';
import { usePaymentMethods } from '../../../hooks/usePaymentMethods';
import type { PaymentMethod } from '../../../types/models/PaymentMethod';
import type {
  ApiResponsePaymentMethodDelete,
  ApiResponsePaymentMethodShow,
} from '../../../types/api/ApiResponsePaymentMethod';
import {
  CardStatusLabels,
  IMPERSONATION_MONEY_NOTICE,
  describeCard,
  formatCardExpiry,
  isUsableCard,
} from './paymentDisplay';
import AddCardModal from './AddCardModal';

interface SavedCardsCardProps {
  /** Called after a card was set default / removed so the wallet card can refresh its default card. */
  onChanged?: () => void;
}

/**
 * "Saved cards" — the tenant's Mollie card mandates (docs/api-types/payment-methods.md): list,
 * set-default, remove, add. Every write is refused by the backend under an impersonation session
 * (a staff member never touches a customer's instruments), so those controls are not rendered then.
 */
const SavedCardsCard: React.FunctionComponent<SavedCardsCardProps> = ({ onChanged }) => {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const { isImpersonating } = useImpersonation();
  const { paymentMethods, loading, error, refetch } = usePaymentMethods();

  const [isAddOpen, setIsAddOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<PaymentMethod | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const notify = useCallback(
    (variant: 'success' | 'danger', title: string, description: string) => {
      addNotification({
        title,
        srTitle: variant === 'success' ? 'Success alert' : 'Error alert',
        description,
        variant,
      });
      setDrawerOpen(true);
    },
    [addNotification, setDrawerOpen],
  );

  const setDefault = useCallback(
    async (card: PaymentMethod) => {
      setBusyId(card.id);
      try {
        const response = await apiFetch(`/clients/payment-methods/${card.id}/default`, { method: 'PUT' });
        // JSend: branch on body.status — errors arrive at HTTP 200 with a fixed message.
        const body = (await response.json().catch(() => null)) as ApiResponsePaymentMethodShow | null;
        if (body?.status === 'success' && body.data?.payment_method) {
          notify('success', 'Default card updated', `${describeCard(card)} is now your default card.`);
          await refetch();
          onChanged?.();
          return;
        }
        notify('danger', 'Could not change the default card', body?.message ?? 'Please try again.');
      } catch (err) {
        notify(
          'danger',
          'Could not change the default card',
          err instanceof Error ? err.message : 'An unexpected network error occurred.',
        );
      } finally {
        setBusyId(null);
      }
    },
    [apiFetch, notify, refetch, onChanged],
  );

  const removeCard = useCallback(async () => {
    if (removeTarget === null) {
      return;
    }
    const card = removeTarget;
    setBusyId(card.id);
    try {
      const response = await apiFetch(`/clients/payment-methods/${card.id}`, { method: 'DELETE' });
      const body = (await response.json().catch(() => null)) as ApiResponsePaymentMethodDelete | null;
      if (body?.status === 'success' && body.data?.revoked === true) {
        notify('success', 'Card removed', `${describeCard(card)} was removed. Add it again to reuse it.`);
        setRemoveTarget(null);
        await refetch();
        onChanged?.();
        return;
      }
      notify('danger', 'Could not remove the card', body?.message ?? 'Please try again.');
    } catch (err) {
      notify(
        'danger',
        'Could not remove the card',
        err instanceof Error ? err.message : 'An unexpected network error occurred.',
      );
    } finally {
      setBusyId(null);
    }
  }, [apiFetch, notify, refetch, onChanged, removeTarget]);

  const buildRowActions = (card: PaymentMethod): IAction[] => {
    const actions: IAction[] = [];
    const busy = busyId !== null;
    if (!card.is_default) {
      actions.push({
        title: 'Set as default',
        onClick: () => void setDefault(card),
        ...(!isUsableCard(card)
          ? { isAriaDisabled: true, tooltipProps: { content: 'Only a valid, unexpired card can be the default' } }
          : busy
            ? { isAriaDisabled: true, tooltipProps: { content: 'Please wait…' } }
            : {}),
      });
    }
    actions.push({
      title: 'Remove',
      onClick: () => setRemoveTarget(card),
      ...(busy ? { isAriaDisabled: true, tooltipProps: { content: 'Please wait…' } } : {}),
    });
    return actions;
  };

  const renderBody = () => {
    if (loading && paymentMethods.length === 0 && !error) {
      return (
        <Bullseye>
          <Spinner size="lg" aria-label="Loading your saved cards" />
        </Bullseye>
      );
    }
    if (error) {
      return (
        <Alert
          variant="danger"
          isInline
          title="Unable to load your saved cards"
          actionLinks={
            <Button variant="link" isInline onClick={() => void refetch()}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      );
    }
    if (paymentMethods.length === 0) {
      return (
        <EmptyState titleText="No saved card yet" icon={CreditCardIcon} variant="sm" headingLevel="h3">
          <EmptyStateBody>
            Save a card by ticking &quot;Save this card&quot; when you pay at checkout, or register one now with
            &quot;Add a card&quot;. A saved card can then pay invoices, top-ups and domain orders in one click.
          </EmptyStateBody>
        </EmptyState>
      );
    }
    return (
      <Table aria-label="Saved cards" variant="compact" borders={false}>
        <Thead>
          <Tr>
            <Th>Card</Th>
            <Th>Holder</Th>
            <Th>Expires</Th>
            <Th>Status</Th>
            {!isImpersonating && <Th screenReaderText="Actions" />}
          </Tr>
        </Thead>
        <Tbody>
          {paymentMethods.map((card) => (
            <Tr key={card.id}>
              <Td dataLabel="Card">{describeCard(card)}</Td>
              <Td dataLabel="Holder">{card.card_holder ?? '—'}</Td>
              <Td dataLabel="Expires">{formatCardExpiry(card)}</Td>
              <Td dataLabel="Status">
                <Flex spaceItems={{ default: 'spaceItemsXs' }}>
                  <FlexItem>
                    <CardStatusLabels card={card} />
                  </FlexItem>
                </Flex>
              </Td>
              {!isImpersonating && (
                <Td dataLabel="Actions" isActionCell>
                  <ActionsColumn
                    items={buildRowActions(card)}
                    actionsToggle={({ onToggle, isOpen, isDisabled, toggleRef }) => (
                      <MenuToggle
                        ref={toggleRef}
                        aria-label={`Actions for ${describeCard(card)}`}
                        variant="plain"
                        onClick={onToggle}
                        isExpanded={isOpen}
                        isDisabled={isDisabled}
                        icon={busyId === card.id ? <Spinner size="md" aria-label="Updating card" /> : <EllipsisVIcon />}
                      />
                    )}
                  />
                </Td>
              )}
            </Tr>
          ))}
        </Tbody>
      </Table>
    );
  };

  return (
    <>
      <Card isFullHeight>
        <CardHeader
          actions={{
            actions: (
              <>
                <Button
                  variant="secondary"
                  icon={<SyncAltIcon />}
                  onClick={() => void refetch()}
                  isLoading={loading}
                  isDisabled={loading}
                  aria-label="Refresh saved cards"
                >
                  Refresh
                </Button>
                {!isImpersonating && (
                  <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsAddOpen(true)}>
                    Add a card
                  </Button>
                )}
              </>
            ),
          }}
        >
          <CardTitle>Saved cards</CardTitle>
        </CardHeader>
        <CardBody>
          {isImpersonating && (
            <Alert variant="info" isInline title="Card management unavailable" className="pf-v6-u-mb-md">
              {IMPERSONATION_MONEY_NOTICE}
            </Alert>
          )}
          {renderBody()}
        </CardBody>
      </Card>

      <AddCardModal isOpen={isAddOpen} onClose={() => setIsAddOpen(false)} />

      {removeTarget && (
        <Modal
          variant="small"
          isOpen
          onClose={() => (busyId !== null ? undefined : setRemoveTarget(null))}
          aria-label="Remove card confirmation"
        >
          <ModalHeader title="Remove this card?" titleIconVariant="warning" />
          <ModalBody>
            {describeCard(removeTarget)} (expires {formatCardExpiry(removeTarget)}) will be revoked at our payment
            provider. This is <strong>permanent</strong>: to use this card again later you will have to add it again.
            {removeTarget.is_default && ' Another valid card, if any, becomes your default card.'}
          </ModalBody>
          <ModalFooter>
            <Button
              variant="danger"
              onClick={() => void removeCard()}
              isLoading={busyId !== null}
              isDisabled={busyId !== null}
            >
              Remove card
            </Button>
            <Button variant="link" onClick={() => setRemoveTarget(null)} isDisabled={busyId !== null}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </>
  );
};

export default SavedCardsCard;
